import React, {useEffect, useState} from 'react';
import DetailsModal from '../../../Presentation/components/common/DetailsModal';
import Modal from '../../../Presentation/components/ui/Modal';
import ImageViewer from '../../../Presentation/components/common/ImageViewer';
import {AppTitle, AppCardTitle, AppSectionTitle, AppText, AppBodyText, AppSmallText, AppCaption} from '../../../Presentation/components/ui/Typography';
import {Visit} from '../models/visit.model';
import {MapPin, User, Calendar, Package, FileText, CheckCircle, XCircle, Loader2, AlertCircle, ExternalLink} from 'lucide-react';
import Button from '../../../Presentation/components/ui/Button';
import {formatDate} from '../../../Presentation/utils/dateUtils';
import {useVisitProductDetails} from '../hooks/useVisitProductDetails';
import {useEnabledModules} from '../../../core/context/ClientContext';
import PermissionGate from '../../../Presentation/components/common/PermissionGate';
import {PERMISSIONS} from '../../../constants/permissions';
import VisitChecklistsSection from './VisitChecklistsSection';

interface VisitDetailsModalProps {
  visit: Visit | null;
  isOpen: boolean;
  onClose: () => void;
  onApproveVisit?: (visit: Visit) => void;
  onRejectVisit?: (visit: Visit, reason: string) => void;
  actionError?: string | null;
  actionErrorDetails?: {product_id?: number; current_quantity?: number; reported_quantity?: number} | null;
  onClearError?: () => void;
  actionLoading?: { approve: boolean; reject: boolean };
}

const MIN_REJECT_REASON_LENGTH = 3;

const VisitDetailsModal: React.FC<VisitDetailsModalProps> = ({
  visit,
  isOpen,
  onClose,
  onApproveVisit,
  onRejectVisit,
  actionError,
  actionErrorDetails,
  onClearError,
  actionLoading = { approve: false, reject: false }
}) => {
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const {productDetails, loading: productsLoading, error: productsError, fetchProductDetails} = useVisitProductDetails();
  const checklistsEnabled = useEnabledModules().includes('checklists');

  const handleRejectClick = () => setRejectModalOpen(true);
  const handleRejectModalClose = () => {
    setRejectModalOpen(false);
    setRejectReason('');
  };
  const handleRejectSubmit = () => {
    const trimmed = rejectReason.trim();
    if (visit && trimmed.length >= MIN_REJECT_REASON_LENGTH) {
      onRejectVisit?.(visit, trimmed);
      handleRejectModalClose();
    }
  };

  // Fetch product details when visit changes (only if modal is open and visit has items)
  // This is optional - product names are fetched to display friendly names instead of IDs
  useEffect(() => {
    if (isOpen && visit?.visitItems && visit.visitItems.length > 0) {
      const productIds = visit.visitItems.map(item => item.productId);
      // Only fetch if we don't already have the product details
      const missingProductIds = productIds.filter(id => !productDetails[id]);
      if (missingProductIds.length > 0) {
        fetchProductDetails(missingProductIds);
      }
    }
  }, [isOpen, visit?.visitItems, fetchProductDetails, productDetails]);
  if (!visit) {
    return null;
  }

  // Prepare action buttons for the modal
  const actionButtons = [];

  if (visit.status === 'pending') {
    actionButtons.push(
      {
        label: 'موافقة',
        onClick: () => onApproveVisit?.(visit),
        variant: 'primary' as const,
        color: 'green' as const,
        icon: <CheckCircle className="h-4 w-4" />
      },
      {
        label: 'رفض',
        onClick: handleRejectClick,
        variant: 'outline' as const,
        color: 'red' as const,
        icon: <XCircle className="h-4 w-4" />
      }
    );
  }

  // Prepare images for ImageViewer (support url from fileKey if needed)
  const visitImages = visit.visitImages?.map(img => ({
    id: img.id,
    imageUrl: img.imageUrl,
    description: img.description ?? img.watermarkText ?? '',
    imageType: img.imageType ?? ''
  })) || [];

  const mapsLink = visit.startLocation
    ? `https://www.google.com/maps?q=${visit.startLocation.lat},${visit.startLocation.lng}`
    : visit.store?.latitude != null && visit.store?.longitude != null &&
      !(visit.store.latitude === 0 && visit.store.longitude === 0)
      ? `https://www.google.com/maps?q=${visit.store.latitude},${visit.store.longitude}`
      : null;
  const isVisitLocation = !!visit.startLocation;

  return (
    <DetailsModal
      isOpen={isOpen}
      onClose={onClose}
      title={`تفاصيل الزيارة #${visit.id}`}
      width="max-w-4xl"
      actionButtons={actionButtons.map(btn => ({...btn, disabled: actionLoading.approve || actionLoading.reject}))}
      loading={actionLoading.approve || actionLoading.reject}
    >
      <div className="space-y-8">
        {/* Error Display */}
        {actionError && (
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <AppText className="font-semibold text-red-900 mb-1">خطأ في العملية</AppText>
                <AppBodyText className="text-red-800">{actionError}</AppBodyText>
                {actionErrorDetails && (
                  <div className="mt-2 p-2 bg-red-100 rounded-lg border border-red-200">
                    {actionErrorDetails.product_id && (
                      <AppSmallText className="text-red-700 block">
                        المنتج المتأثر: #{actionErrorDetails.product_id}
                      </AppSmallText>
                    )}
                    {actionErrorDetails.current_quantity !== undefined && (
                      <AppSmallText className="text-red-700 block">
                        الكمية الحالية في النظام: {actionErrorDetails.current_quantity}
                      </AppSmallText>
                    )}
                    {actionErrorDetails.reported_quantity !== undefined && (
                      <AppSmallText className="text-red-700 block">
                        الكمية المبلغ عنها: {actionErrorDetails.reported_quantity}
                      </AppSmallText>
                    )}
                  </div>
                )}
              </div>
              {onClearError && (
                <button
                  onClick={onClearError}
                  className="text-red-400 hover:text-red-600 transition-colors"
                  aria-label="إغلاق"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Header Section with Status */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-brand-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-brand-100 rounded-full">
                <Calendar className="h-6 w-6 text-brand-600" />
              </div>
              <div>
                <AppTitle className="text-gray-900">تفاصيل الزيارة</AppTitle>
                <AppBodyText className="text-gray-600">تم إنشاؤها في {formatDate(visit.createdAt)}</AppBodyText>
              </div>
            </div>
            <div className="text-right">
              <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${visit.statusColor}`}>
                {visit.statusText}
              </span>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Store Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-brand-100 rounded-lg">
                <MapPin className="h-5 w-5 text-brand-600" />
              </div>
              <AppCardTitle>معلومات المتجر</AppCardTitle>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                <div>
                  <AppSmallText className="text-gray-600">اسم المتجر</AppSmallText>
                  <AppText className="font-semibold text-gray-900">{visit.store.storeName}</AppText>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <AppCaption className="text-gray-500 mb-1">المدينة</AppCaption>
                  <AppText className="font-medium">{visit.store.city}</AppText>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <AppCaption className="text-gray-500 mb-1">المنطقة</AppCaption>
                  <AppText className="font-medium">{visit.store.region}</AppText>
                </div>
              </div>
              {visit.store.managerName && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <AppSmallText className="text-green-600 mb-1">مدير المتجر</AppSmallText>
                  <AppText className="font-medium text-green-900">{visit.store.managerName}</AppText>
                </div>
              )}
              {visit.store.phoneNumber && (
                <div className="p-3 bg-brand-50 rounded-lg border border-brand-200">
                  <AppSmallText className="text-brand-600 mb-1">رقم الهاتف</AppSmallText>
                  <AppText className="font-medium text-brand-900">{visit.store.phoneNumber}</AppText>
                </div>
              )}
            </div>
          </div>

          {/* User Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 rounded-lg">
                <User className="h-5 w-5 text-green-600" />
              </div>
              <AppCardTitle>معلومات المسوق</AppCardTitle>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <div>
                  <AppBodyText className="text-gray-600">الاسم الكامل</AppBodyText>
                  <AppText className="font-semibold text-gray-900">{visit.visitor.fullName}</AppText>
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <AppSmallText className="text-gray-500 mb-1">اسم المستخدم</AppSmallText>
                <AppText className="font-medium">{visit.visitor.username}</AppText>
              </div>
              {visit.visitor.email && (
                <div className="p-3 bg-brand-50 rounded-lg border border-brand-200">
                  <AppSmallText className="text-brand-600 mb-1">البريد الإلكتروني</AppSmallText>
                  <AppText className="font-medium text-brand-900">{visit.visitor.email}</AppText>
                </div>
              )}
              {visit.visitor.phoneNumber && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <AppSmallText className="text-green-600 mb-1">رقم الهاتف</AppSmallText>
                  <AppText className="font-medium text-green-900">{visit.visitor.phoneNumber}</AppText>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Reject reason when rejected */}
        {visit.status === 'rejected' && visit.rejectReason && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <XCircle className="h-5 w-5 text-red-600" />
              <AppCardTitle className="text-red-900">سبب الرفض</AppCardTitle>
            </div>
            <AppBodyText className="text-red-800">{visit.rejectReason}</AppBodyText>
          </div>
        )}

        {/* Visit Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white border border-brand-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Calendar className="h-5 w-5 text-brand-600" />
              </div>
              <AppCardTitle>تاريخ الزيارة</AppCardTitle>
            </div>
            <div className="space-y-3">
              {(visit.startedAt ?? visit.visitDate) && (
                <div className="p-4 bg-brand-50 rounded-lg">
                  <AppBodyText className="text-brand-600 mb-1">بداية الزيارة</AppBodyText>
                  <AppSectionTitle className="font-semibold text-brand-900">
                    {formatDate(visit.startedAt ?? visit.visitDate)}
                  </AppSectionTitle>
                </div>
              )}
              <div className="p-3 bg-gray-50 rounded-lg">
                <AppSmallText className="text-gray-500 mb-1">تم الإنشاء</AppSmallText>
                <AppText className="font-medium text-gray-700">{formatDate(visit.createdAt)}</AppText>
              </div>
            </div>
          </div>

          {mapsLink && (
            <a
              href={mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border border-emerald-200 rounded-xl p-6 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all group"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                  <MapPin className="h-5 w-5 text-emerald-600" />
                </div>
                <AppCardTitle>الموقع</AppCardTitle>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-2xl font-bold text-emerald-600 mb-2">
                  <ExternalLink className="h-5 w-5" />
                  عرض على الخريطة
                </div>
                <AppBodyText className="text-gray-600">
                  {isVisitLocation ? 'مكان بدء الزيارة' : 'موقع المتجر'} — انقر لفتح على الخريطة
                </AppBodyText>
              </div>
            </a>
          )}

          <div className="bg-white border border-purple-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <AppCardTitle>المنتجات</AppCardTitle>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">{visit.visitItems?.length || 0}</div>
              <AppBodyText className="text-gray-600">منتج محدث</AppBodyText>
            </div>
          </div>

          <div className="bg-white border border-orange-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FileText className="h-5 w-5 text-orange-600" />
              </div>
              <AppCardTitle>الصور</AppCardTitle>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 mb-2">{visit.visitImages?.length || 0}</div>
              <AppBodyText className="text-gray-600">صورة مرفقة</AppBodyText>
            </div>
          </div>
        </div>

        {/* Visit Items Details */}
        {visit.visitItems && visit.visitItems.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <AppSectionTitle>تفاصيل المنتجات</AppSectionTitle>
              <div className="ml-auto">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  {visit.visitItems.length} منتج
                </span>
              </div>
            </div>

            {/* Product Loading Error */}
            {productsError && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AppBodyText className="text-yellow-800">
                  تحذير: لا يمكن تحميل تفاصيل بعض المنتجات. سيتم عرض معرفات المنتجات بدلاً من الأسماء.
                </AppBodyText>
              </div>
            )}
            <div className="grid gap-4">
              {visit.visitItems.map((item, index) => {
                const product = productDetails[item.productId];
                const productName = product?.name || item.productName || `منتج #${item.productId}`;
                const brandName = visit.brand?.name || `العلامة التجارية #${item.brandId}`;

                // Check if this product has an error
                const hasError = actionErrorDetails?.product_id === item.productId;

                return (
                  <div
                    key={item.id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      hasError
                        ? 'bg-red-50 border-red-300 shadow-md'
                        : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          {productsLoading ? (
                            <Loader2 className="h-4 w-4 text-purple-600 animate-spin" />
                          ) : (
                            <span className="text-sm font-bold text-purple-600">{index + 1}</span>
                          )}
                        </div>
                        <div>
                          <AppText className="font-semibold text-gray-900">{productName}</AppText>
                          <AppBodyText className="text-gray-600">العلامة التجارية: {brandName}</AppBodyText>
                        </div>
                      </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.availability && (
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          item.availability === 'AVAILABLE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {item.availability === 'AVAILABLE' ? 'متوفر' : 'غير متوفر'}
                        </span>
                      )}
                      {item.availability === 'AVAILABLE' && (item.quantity ?? item.newQuantity ?? item.previousQuantity ?? 0) != null && (
                        <span className="text-gray-600 text-sm font-medium">الكمية: {item.quantity ?? item.newQuantity ?? item.previousQuantity ?? 0}</span>
                      )}
                    </div>
                  </div>

                  {item.notes && (
                    <div className="mt-3 p-3 bg-brand-50 rounded-lg border border-brand-200">
                      <AppSmallText className="text-brand-600 font-medium mb-1">ملاحظات</AppSmallText>
                      <AppBodyText className="text-brand-800">{item.notes}</AppBodyText>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes */}
        {visit.notes && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-brand-100 rounded-lg">
                <FileText className="h-5 w-5 text-brand-600" />
              </div>
              <AppSectionTitle>الملاحظات</AppSectionTitle>
            </div>
            <div className="p-4 bg-brand-50 rounded-lg border border-brand-200">
              <AppText className="text-gray-800 leading-relaxed">{visit.notes}</AppText>
            </div>
          </div>
        )}

        {/* Checklists filled during the visit (CR09 Phase 2 — Salam; gated by
            the checklists module + checklist_submissions.read) */}
        <PermissionGate permission={PERMISSIONS.READ_CHECKLIST_SUBMISSIONS}>
          <VisitChecklistsSection visitId={visit.id} enabled={checklistsEnabled} />
        </PermissionGate>

        {/* Images */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FileText className="h-5 w-5 text-orange-600" />
            </div>
            <AppSectionTitle>صور الزيارة</AppSectionTitle>
            <div className="ml-auto">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                {visitImages.length} صورة
              </span>
            </div>
          </div>
          <ImageViewer
            images={visitImages}
            title=""
            emptyMessage="لا توجد صور لهذه الزيارة"
            showThumbnails={true}
            thumbnailSize="medium"
            className="bg-gray-50 rounded-lg p-4"
          />
          {visit.visitImages && visit.visitImages.filter(img => img.capturedAt || img.watermarkText || img.location).length > 0 && (
            <div className="mt-4 space-y-2">
              {visit.visitImages
                .filter(img => img.capturedAt || img.watermarkText || img.location)
                .map((img) => (
                  <div key={img.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
                {img.capturedAt && (
                  <AppSmallText className="text-gray-600 block">التاريخ: {formatDate(img.capturedAt)}</AppSmallText>
                )}
                {img.watermarkText && (
                  <AppSmallText className="text-gray-600 block">العلامة المائية: {img.watermarkText}</AppSmallText>
                )}
                {img.location && (img.location.lat != null || img.location.lng != null) && (
                  <a
                    href={`https://www.google.com/maps?q=${img.location.lat ?? ''},${img.location.lng ?? ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-brand-600 hover:underline"
                  >
                    <MapPin className="h-3 w-3" /> عرض موقع الصورة
                  </a>
                )}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Reject Reason Modal */}
      <Modal
        open={rejectModalOpen}
        onClose={handleRejectModalClose}
        title="سبب رفض الزيارة"
        width="max-w-md"
      >
        <div className="space-y-4">
          <AppBodyText className="text-gray-600">
            يرجى إدخال سبب الرفض (3 أحرف على الأقل)
          </AppBodyText>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="سبب الرفض..."
            className="w-full min-h-[100px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            dir="rtl"
          />
          <div className="flex gap-3 justify-end">
            <Button variant="ghost" onClick={handleRejectModalClose} disabled={actionLoading.reject}>
              إلغاء
            </Button>
            <Button
              variant="danger"
              onClick={handleRejectSubmit}
              disabled={rejectReason.trim().length < MIN_REJECT_REASON_LENGTH || actionLoading.reject}
              isLoading={actionLoading.reject}
            >
              رفض الزيارة
            </Button>
          </div>
        </div>
      </Modal>
    </DetailsModal>
  );
};

export default VisitDetailsModal;
